import getPreferences from 'api/getPreferences'
import { PlausibleEvent } from 'enums/plausible'
import { useEffect, useState } from 'react'
import { Terms } from 'static/regulation'
import usePlausible from 'utils/hooks/usePlausible'
import { useInfoProviderContext } from 'utils/providers/InfoProvider/InfoProviderContext'

import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import dynamic from 'next/dynamic'
import Head from 'next/head'

import PageLayout from 'layouts/PageLayout'
import { getTitleLocation, MetaTypeNames } from 'pages'

import Markdown from 'components/Markdown/Markdown'
import PageSelector from 'components/PageSelector/PageSelector'
import StructuredData from 'components/StructuredData/StructuredData'
import useIsQVariant from 'utils/hooks/useIsQVariant'

const DynamicRestrictedModal = dynamic(() => import('components/RestrictedModal'))

const CustomerAgreement = ({
  referrer,
  ssrHost,
}: {
  referrer: string
  ssrHost: string
}) => {
  const { t } = useTranslation(['regulation', 'seo', 'shared', 'contacts', 'main'])

  const [wagerPercent, setWagerPercent] = useState(20)

  const isQ = useIsQVariant()

  const setEvent = usePlausible()

  useEffect(() => {
    setEvent(PlausibleEvent.OpenedAgreement, {
      step: 1,
      referrer,
    })
  }, [])

  useEffect(() => {
    const getAppPreferences = async () => {
      const res = await getPreferences()
      setWagerPercent(res.data.payload?.wagerPercent || 20)
    }

    getAppPreferences()
  }, [setWagerPercent])

  const info = useInfoProviderContext()

  if (info?.restricted === true) {
    return <DynamicRestrictedModal />
  }
  return (
    <>
      <StructuredData
        host={ssrHost}
        variant="reduced"
        path="/regulation/terms-and-conditions"
        pageTitle={t(`seo:${getTitleLocation(ssrHost, 'agreement', MetaTypeNames.Title)}`)}
      />
      <Head>
        {/* t(`seo:agreementTitle`) */}
        {/* t(`seo:agreementTitle24`) */}
        <title>{t(`seo:${getTitleLocation(ssrHost, 'agreement', MetaTypeNames.Title)}`)}</title>
        {/* t(`seo:agreementDescription`) */}
        {/* t(`seo:agreementDescription24`) */}
        <meta
          name="description"
          content={t(`seo:${getTitleLocation(ssrHost, 'agreement', MetaTypeNames.Description)}`)}
        />

        <meta
          property="og:title"
          content={t(`seo:${getTitleLocation(ssrHost, 'agreement', MetaTypeNames.Title)}`)}
        />
        <meta
          property="og:description"
          content={t(`seo:${getTitleLocation(ssrHost, 'agreement', MetaTypeNames.Description)}`)}
        />
        <meta
          property="og:image"
          content={`https://${ssrHost}/images/ogImages/regulationAgreement.png`}
        />
        <meta property="og:site_name" content="Binany - Online Platform for Trading" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://${ssrHost}/regulation/agreement`} />
      </Head>
      <PageLayout
        subtitle={t('main:whatisInTheAgreement')}
        title={t('shared:navigationAgreement')}
        breadcrumbs={[
          { title: t('main:binanyTrading'), link: '/' },
          { title: t('shared:navigationAgreement'), link: '/regulation/terms-and-conditions' },
        ]}
        host={ssrHost}
      >
        <PageSelector
          pages={[
            { title: t('shared:navigationWhatis'), link: '/regulation/whatis', name: 'whatis' },
            {
              title: t('shared:navigationPrivacy'),
              link: isQ ? '/q/regulation/privacy-policy' : '/regulation/privacy-policy',
              name: 'privacy',
            },
            {
              title: t('shared:navigationTerms'),
              link: isQ ? '/q/regulation/terms-and-conditions' : '/regulation/terms-and-conditions',
              name: 'agreement',
            },
          ]}
          pageName="agreement"
          referrer={referrer}
          selectedPage={{
            title: t('shared:navigationTerms'),
            link: isQ ? '/q/regulation/terms-and-conditions' : '/regulation/terms-and-conditions',
            name: 'agreement',
          }}
        />
        <Markdown dir="ltr" scrollable={false} unnumerableList>
          {Terms}
        </Markdown>
      </PageLayout>
    </>
  )
}

export default CustomerAgreement

export async function getServerSideProps({ locale, req }) {
  return {
    props: {
      ssrHost: req.headers.host || '',
      ...(await serverSideTranslations(locale, [
        'regulation',
        'seo',
        'shared',
        'contacts',
        'main',
      ])),
    },
  }
}
